<script setup lang="ts">
    import { nextTick, ref, useId, watch } from 'vue'
    import { useI18n } from 'vue-i18n'

    const props = defineProps<{
        /**
         * Confirmed offline, not just `navigator.onLine`: this state covers the
         * whole app, so the caller must verify before switching it on (see
         * `useConnectivity`).
         */
        offline?: boolean
        /** A connectivity check is in flight. */
        checking?: boolean
    }>()

    const emit = defineEmits<{
        retry: []
    }>()

    const { t } = useI18n()

    const titleId = useId()
    const textId = useId()

    // The screen has just covered whatever the user was doing, so focus follows
    // it: without this, tabbing keeps walking the app hidden underneath.
    const panel = ref<HTMLElement>()
    watch(
        () => props.offline,
        async (value) => {
            if (!value) {
                return
            }
            await nextTick()
            panel.value?.querySelector('button')?.focus()
        },
    )
</script>

<template>
    <Transition name="pk-offline-screen">
        <div
            v-if="offline"
            class="pk-offline-screen"
            role="alertdialog"
            :aria-labelledby="titleId"
            :aria-describedby="textId">
            <!-- Texture only at the edges: the dots are masked out around the
                 middle so they never sit behind the text. -->
            <div class="pk-offline-screen__texture bg-dot" aria-hidden="true" />

            <div ref="panel" class="pk-offline-screen__panel">
                <span class="pk-offline-screen__badge" aria-hidden="true">
                    <VvIcon name="wifi-off" />
                </span>

                <h1
                    :id="titleId"
                    class="vv-text vv-text--headline vv-text--size-1 pk-offline-screen__title">
                    {{ t('title') }}
                </h1>

                <p :id="textId" class="pk-offline-screen__text">
                    {{ t('text') }}
                </p>

                <hr class="pk-offline-screen__divider" />

                <div class="pk-offline-screen__actions">
                    <VvButton
                        modifiers="primary"
                        icon="reload"
                        :label="$t('action.retry')"
                        :loading="checking"
                        :disabled="checking"
                        @click="emit('retry')" />

                    <p class="pk-offline-screen__status" aria-live="polite">
                        <span
                            class="pk-offline-screen__pulse"
                            aria-hidden="true" />
                        {{ checking ? t('statusChecking') : t('statusIdle') }}
                    </p>
                </div>
            </div>
        </div>
    </Transition>
</template>

<style lang="scss">
    @layer volver.components {
        .pk-offline-screen {
            position: fixed;
            inset: 0;
            z-index: var(--z-notification-alert);
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: var(--color-surface);

            // Keeps the panel clear of the notch and the home indicator, which
            // the `viewport-fit=cover` viewport lets the app paint under.
            padding: calc(env(safe-area-inset-top) + var(--spacing-lg))
                calc(env(safe-area-inset-right) + var(--spacing-lg))
                calc(env(safe-area-inset-bottom) + var(--spacing-lg))
                calc(env(safe-area-inset-left) + var(--spacing-lg));

            &__texture {
                position: absolute;
                inset: 0;
                mask-image: radial-gradient(
                    70% 60% at 50% 50%,
                    transparent 35%,
                    black
                );
            }

            &__panel {
                position: relative;
                width: 100%;

                // Holds the copy to a readable measure (roughly 60 characters)
                // without padding eating into it.
                max-width: var(--spacing-384);
            }

            &__badge {
                display: flex;
                align-items: center;
                justify-content: center;
                width: var(--spacing-48);
                height: var(--spacing-48);
                margin-bottom: var(--spacing-24);
                border: var(--border) solid var(--color-surface-3);
                border-radius: var(--rounded);
                background-color: var(--color-surface-1);
                color: var(--color-word-2);
                font-size: var(--text-24);
            }

            // The headline modifier ships its own margins: the panel drives the
            // vertical rhythm instead.
            &__title {
                margin: 0;
                text-wrap: balance;
            }

            &__text {
                margin: var(--spacing-8) 0 0;
                color: var(--color-word-1);
                line-height: var(--leading-relaxed);
                text-wrap: pretty;
            }

            &__divider {
                margin: var(--spacing-32) 0 var(--spacing-24);
                border: 0;
                border-top: var(--border) solid var(--color-surface-3);
            }

            &__actions {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                gap: var(--spacing-12) var(--spacing-16);
            }

            &__status {
                display: flex;
                align-items: center;
                gap: var(--spacing-8);
                margin: 0;
                color: var(--color-word-3);
                font-size: var(--text-12);
            }

            // Semantic, and the only colour on the screen: something is still
            // watching the connection.
            &__pulse {
                flex-shrink: 0;
                width: var(--spacing-8);
                height: var(--spacing-8);
                border-radius: 50%;
                background-color: var(--color-warning-darken-2);
                animation: pk-offline-screen-pulse 2s ease-in-out infinite;
            }

            &-enter-active,
            &-leave-active {
                transition: opacity 0.2s ease-out;
            }

            &-enter-from,
            &-leave-to {
                opacity: 0;
            }
        }

        @keyframes pk-offline-screen-pulse {
            0%,
            100% {
                opacity: 1;
                transform: scale(1);
            }

            50% {
                opacity: var(--opacity-40);
                transform: scale(0.8);
            }
        }

        @media (prefers-reduced-motion: reduce) {
            .pk-offline-screen {
                &-enter-active,
                &-leave-active {
                    transition: none;
                }

                &__pulse {
                    animation: none;
                }
            }
        }
    }
</style>

<i18n lang="json">
{
    "en": {
        "title": "You are offline",
        "text": "Without a connection Agent Studio cannot load or save anything.",
        "statusIdle": "Checking again every few seconds",
        "statusChecking": "Checking the connection"
    },
    "it": {
        "title": "Sei offline",
        "text": "Senza connessione Agent Studio non può caricare né salvare nulla.",
        "statusIdle": "Ricontrollo ogni pochi secondi",
        "statusChecking": "Sto ricontrollando la connessione"
    }
}
</i18n>
